import { useCallback } from 'react';
import { useBookSpot } from './useBookSpot';
import { InteractiveSpotEvent } from '../../types/analytics';
import { Loader } from '../loader/Loader';
import { WarningAlert } from '../warning-alert/WarningAlert';
import { ModalHeader } from '../modal-header/ModalHeader';
import { ActionButton } from '../action-button/ActionButton';
import type { BookSpotData } from './BookSpot.types';

type BookSpotProps = {
  data: BookSpotData;
};

function BookSpotContent({ data }: { data: BookSpotData }) {
  const {
    title,
    guestBookings,
    selectPreferredLabel,
    selectPreferredWithFriendLabel,
    ctaLabel,
    skipLabel,
    layoutErrorTitle,
    bookingFailedMessage,
    spotUnavailableTitle,
    spotAlreadyTakenMessage,
  } = data;

  const renderLoading = useCallback(() => <Loader />, []);

  const {
    transactionInProgress,
    hasSucceeded,
    error,
    layoutUnavailable,
    spotTaken,
    trackSpotEvent,
    roomLayoutMapData,
    handleCloseFromSpotSelection,
    confirm,
    buttonActionMode,
    layoutWarningLabels,
  } = useBookSpot(data, renderLoading);

  const onSpotSelectionClose = useCallback(() => {
    trackSpotEvent(InteractiveSpotEvent.MODAL_DISMISSED_WITHOUT_SPOT);
    handleCloseFromSpotSelection();
  }, [trackSpotEvent, handleCloseFromSpotSelection]);

  const confirmDisabled =
    transactionInProgress || hasSucceeded || buttonActionMode === 'disabled';

  const preferredLabel =
    (guestBookings ?? 0) > 0 ? selectPreferredWithFriendLabel : selectPreferredLabel;

  const RoomLayoutMap =
    typeof window !== 'undefined' ? window.WebReactComponents?.RoomLayoutMap : undefined;

  const warningLabels = layoutWarningLabels();

  return (
    <>
      {transactionInProgress ? (
        <div className="loader-spinner">
          <Loader size="large" />
        </div>
      ) : null}
      <ModalHeader
        data={{
          title,
          onClose: onSpotSelectionClose,
          closeDisabled: transactionInProgress || hasSucceeded,
          dismissModal: false,
        }}
      />
      <div className="modal-body modal-bookbuy">
        <div className="row">
          <div className="content-group">
            {layoutUnavailable || error ? (
              <div className="col s12">
                <WarningAlert
                  title={error ? layoutErrorTitle : warningLabels.title}
                  message={error ? bookingFailedMessage : warningLabels.message}
                />
              </div>
            ) : (
              <>
                <div className="col s12">
                  <div className="title-wrapper">
                    {preferredLabel ? <h4>{preferredLabel}</h4> : null}
                  </div>
                </div>
                <div className="col s12">
                  <div className="book-spot-map">
                    {RoomLayoutMap ? <RoomLayoutMap data={roomLayoutMapData} /> : <Loader />}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="content-group book-spot-cta-group">
            {spotTaken ? (
              <WarningAlert
                title={spotUnavailableTitle}
                message={spotAlreadyTakenMessage}
              />
            ) : null}
            <div className="modal-book-button">
              <ActionButton
                data={{
                  id: 'book-spot-confirm',
                  label: buttonActionMode === 'skip' ? skipLabel : ctaLabel,
                  variant: confirmDisabled ? 'disabled' : 'primary',
                  onClick: confirmDisabled ? undefined : confirm,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function BookSpot({ data }: BookSpotProps) {
  if (!data) {
    return null;
  }

  return <BookSpotContent data={data} />;
}
